/**
 * Step 2 of the downsizing analyser: where the client stands NOT DOWNSIZING, as four numbers, with
 * the sale beside them and how long the money lasts beneath.
 *
 * WHY NOT DOWNSIZING. A downsize is a choice, so the only honest benchmark is life as it is. Every
 * strategy in step 3 is measured against these numbers, in both modes — the same spine as the aged
 * care walk-in and the village's staying put (owner, 24 Sep 2026: "harmonisation of the UI/UX").
 *
 * ONE SOURCE. The pension and cashflow are the standalone annual assessment of the profile as it
 * stands (the same figures the rail's "before" column reads); the estate and the runway come from
 * the stay-put projection — no sale — which is the same run the Suggestion Engine's own baseline
 * candidate makes.
 */
import React from 'react';
import { ProjectionYearsInput } from '../../components/ui/ProjectionYearsInput.tsx';
import { ArrowRight, ChevronLeft, Eye } from 'lucide-react';
import { FundingRunway } from '../../components/narrative/FundingRunway.tsx';
import type { PlanRunwayCopy } from '../../components/insights/fundingSummary.tsx';
import { GradientText } from '../../components/ui/index.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { RATES_PROVENANCE } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { PlainWordsCard } from '../../components/narrative/PlainWords.tsx';
import { MeansTestBeats } from '../../components/insights/MeansTestPosition.tsx';

interface DzStayPutHeroProps {
    factFind: DraftFactFind;
    /** Today's standalone assessment — pension, cashflow, net worth. Null while it loads. */
    todayFinancials: { socialSecurity?: number; cashFlow?: number; netWorth?: number; socialSecurityBreakdown?: unknown } | null;
    /** The estate at the horizon, not downsizing. Null until the stay-put projection has run. */
    stayPutLegacy: number | null;
    /** How long the money lasts, not downsizing — struck on the stay-put projection. */
    runway: PlanRunwayCopy | null;
    legacyYears: number;
    salePrice: number;
    sellingCosts: number;
    /** The tile's own "after N years" is editable (owner, 24 Sep 2026). */
    onLegacyYearsChange?: (years: number) => void;
    onBack: () => void;
    onNext: () => void;
    onAssumptions: () => void;
    /** Opens the projections for not downsizing over the legacy horizon. */
    onShowProjections: () => void;
}

const CARD = 'rounded-2xl border border-white/80 bg-white/60 p-6 shadow-lg shadow-gray-200/50 backdrop-blur-xl';

const Tile = ({ label, value, unit, caption, tone }: { label: string; value: string; unit?: string; caption: React.ReactNode; tone: 'cost' | 'good' | 'bad' | 'family' }) => {
    const rule = { cost: 'bg-slate-500', good: 'bg-emerald-400', bad: 'bg-rose-400', family: 'bg-[#0290F5]' }[tone];
    const figure = { cost: 'text-slate-900', good: 'text-emerald-700', bad: 'text-rose-700', family: 'text-slate-900' }[tone];
    return (
        <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-white px-4 pb-4 pt-5 shadow-card">
            <span className={`absolute inset-x-0 top-0 h-1 ${rule}`} aria-hidden />
            <p className="text-[13px] font-semibold text-slate-600">{label}</p>
            <div className="mt-1.5">
                <span className={`text-[28px] font-bold leading-none tabular-nums tracking-tight ${figure}`}>
                    {value}
                    {unit && <span className="ml-1 text-[13px] font-semibold text-slate-500">{unit}</span>}
                </span>
            </div>
            <p className="mt-2 text-[13px] leading-snug text-slate-600">{caption}</p>
        </div>
    );
};

const DzStayPutHero = ({
    factFind, todayFinancials, stayPutLegacy, runway, legacyYears, salePrice, sellingCosts,
    onLegacyYearsChange, onBack, onNext, onAssumptions, onShowProjections,
}: DzStayPutHeroProps) => {
    const name = factFind?.client?.firstName || 'you';
    const isCouple = factFind?.adviceFor === 'couple' && !!factFind?.partner?.firstName;
    const household = isCouple ? `${name} & ${factFind.partner!.firstName}` : name;
    const ready = !!todayFinancials;
    const pension = todayFinancials?.socialSecurity ?? 0;
    const cashFlow = todayFinancials?.cashFlow ?? 0;
    const netWorth = todayFinancials?.netWorth ?? 0;
    const years = Number(legacyYears) || 0;
    const netProceeds = Math.max(0, salePrice - sellingCosts);

    return (
        <div className="space-y-6">
            {/* 1. The four numbers, not downsizing */}
            <div className={CARD}>
                <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400">Step 2 of 3</p>
                <h3 className="mt-1 text-[22px] font-bold leading-tight tracking-tight">
                    <GradientText>Where {household} {isCouple ? 'stand' : 'stands'} today, with no sale.</GradientText>
                </h3>
                <p className="mt-1 text-[14px] text-slate-600">
                    Life as it is. This is <span className="font-semibold text-slate-800">not downsizing</span>: the baseline every
                    way of using the money in step 3 is measured against.
                </p>
                {!ready ? (
                    <div className="mt-5 rounded-xl border border-dashed border-slate-300 bg-slate-50/80 px-4 py-6 text-center text-[14px] text-slate-600">
                        Working out today&apos;s position…
                    </div>
                ) : (
                    <>
                        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                            <Tile label="Age Pension today" value={formatCurrency(Math.round(pension))} unit="a year" caption={pension > 0 ? 'Estimated entitlement on today’s assets and income.' : 'No entitlement on today’s assets and income.'} tone="good" />
                            <Tile
                                label={cashFlow < 0 ? 'Left to find each year' : 'Left over each year'}
                                value={`${cashFlow < 0 ? '−' : '+'}${formatCurrency(Math.abs(Math.round(cashFlow)))}`}
                                caption={cashFlow < 0 ? 'Money in, less money out. Met from savings.' : 'Money in, less money out, in the current home.'}
                                tone={cashFlow < 0 ? 'bad' : 'good'}
                            />
                            <Tile label="Net worth today" value={formatCurrency(Math.round(netWorth))} caption="Everything owned, less everything owed — the home included." tone="cost" />
                            <Tile
                                label="What's left to the family"
                                value={stayPutLegacy != null ? formatCurrency(Math.round(stayPutLegacy)) : '—'}
                                caption={stayPutLegacy != null ? (
                                    onLegacyYearsChange ? (
                                        <ProjectionYearsInput years={years} onChange={onLegacyYearsChange} after=" not downsizing." />
                                    ) : `After ${years} ${years === 1 ? 'year' : 'years'} not downsizing.`
                                ) : 'Not available: the stay-put projection could not be run.'}
                                tone="family"
                            />
                        </div>
                        <p className="mt-3 text-[13px] text-slate-500">
                            Figures use the rates in force from {RATES_PROVENANCE.agePension}. The{' '}
                            <button type="button" onClick={onAssumptions} className="font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900">
                                Assumptions
                            </button>{' '}
                            page lists every one, with its source and date.
                        </p>
                    </>
                )}
            </div>

            {/* The means test, in words: which test sets the pension and what would have to move. */}
            {ready && (
                <div className={CARD}>
                    <PlainWordsCard eyebrow={`Where ${household} ${isCouple ? 'stand' : 'stands'} today`} className="-mt-5 -mx-5 border-0 bg-transparent shadow-none" collapsible>
                        <MeansTestBeats careDepositRelevant={Boolean(factFind?.goals?.aged_care)} financials={todayFinancials} isCouple={factFind?.adviceFor === 'couple'} name={factFind?.client?.firstName || 'you'} alsoMoves={<>is what the freed-up money does to the pension, since the home is exempt and the money is not</>} whereNext="The strategies in step 3" />
                    </PlainWordsCard>
                </div>
            )}

            {/* 2. The sale, beside the position but not in it */}
            {salePrice > 0 && (
                <div className={CARD}>
                    <p className="text-[11px] font-bold uppercase tracking-[0.09em] text-slate-400">What the sale would bring in</p>
                    <p className="mt-1 text-[14px] text-slate-600">
                        None of this is in the figures above. What the new home takes, what rent would cost, and where the rest goes is what step 3 models.
                    </p>
                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-card">
                            <p className="text-[13px] font-semibold text-slate-600">Sale price</p>
                            <p className="mt-1 text-[22px] font-bold tabular-nums text-slate-900">{formatCurrency(salePrice)}</p>
                            <p className="mt-1 text-[13px] text-slate-500">The figure you entered. Your main home is usually free of capital gains tax.</p>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-card">
                            <p className="text-[13px] font-semibold text-slate-600">Selling costs</p>
                            <p className="mt-1 text-[22px] font-bold tabular-nums text-rose-700">−{formatCurrency(sellingCosts)}</p>
                            <p className="mt-1 text-[13px] text-slate-500">Agent, legal and moving costs. They leave the estate on the day.</p>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-card">
                            <p className="text-[13px] font-semibold text-slate-600">Net proceeds</p>
                            <p className="mt-1 text-[22px] font-bold tabular-nums text-slate-900">{formatCurrency(netProceeds)}</p>
                            <p className="mt-1 text-[13px] text-slate-500">Before a new home or any rent. Counted by Centrelink the moment it is not a home.</p>
                        </div>
                    </div>
                </div>
            )}

            {/* 3. How long the money lasts, not downsizing */}
            {ready && runway && (
                <div className={CARD}>
                    <p className="text-[11px] font-bold uppercase tracking-[0.09em] text-slate-400">How long the money lasts, not downsizing</p>
                    <div className="mt-2">
                        <FundingRunway
                            headline={<span className="text-[18px] font-bold leading-tight tracking-tight text-slate-900">{runway.headline}</span>}
                            coveredPct={runway.coveredPct}
                            leftLabel={runway.leftLabel}
                            rightLabel={runway.rightLabel}
                            depleted={runway.depleted}
                            flag={runway.flag}
                        />
                    </div>
                    <p className="mt-2 text-[13px] text-slate-500">{runway.basis}</p>
                    <button
                        type="button"
                        onClick={onShowProjections}
                        className="mt-3 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] font-semibold text-blue-700 hover:border-blue-300 hover:text-blue-900"
                    >
                        <Eye size={15} /> See the projections, year by year
                    </button>
                </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3">
                <button type="button" onClick={onBack} className="inline-flex h-11 items-center gap-2 rounded-lg border border-slate-300 bg-white px-5 text-[14px] font-semibold text-slate-700 hover:border-blue-300 hover:text-blue-900">
                    <ChevronLeft size={16} /> Change the sale figures
                </button>
                <button
                    type="button"
                    onClick={onNext}
                    disabled={!ready}
                    className="inline-flex h-11 items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-teal-500 px-5 text-[14px] font-semibold text-white shadow-sm hover:from-blue-700 hover:to-teal-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    Show me ways to use the money <ArrowRight size={16} />
                </button>
            </div>
        </div>
    );
};

export default DzStayPutHero;
